import { ArrowLeftIcon, CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useParams, useSearchParams } from 'react-router';

import {
  isResultFull,
  isResultOpen,
  isRunPending,
  isRunReadable,
  ResultCardSkeleton,
  SEARCH_RESULTS_PAGE_SIZE,
  useConfirmSearch,
  useSearchResults,
  useSearchRun,
  type ShowcaseSort,
} from '@entities/search-run';
import { stageToStep, useSearchSession } from '@entities/search-session';
import { VehicleCardSkeleton } from '@entities/vehicle/VehicleCardSkeleton';
import {
  isShowcaseSort,
  parseResultQuery,
  resetResultFilters,
  toResultsApiQuery,
  toResultSearchParams,
  toShowcaseQuery,
  toVehicleLinkSearch,
} from '@features/filters/model/resultQuery';
import { KarlDialogue } from '@features/chat';
import {
  EmptyResults,
  FavoritesEmpty,
  FavoritesFilterButton,
  FavoritesOnlyNote,
  PaidResultCard,
  ResultsSummary,
  SearchProgress,
  Showcase,
  StaleNotice,
} from '@features/search';
import { ApiError } from '@shared/api/problem';
import { queryKeys } from '@shared/api/queryKeys';
import { ROUTES } from '@shared/config/routes';
import { useAppShell } from '@shared/lib/appShell';
import { Button, Container, StateBlock } from '@shared/ui';
import { Reveal } from '@shared/ui/Reveal/Reveal';

import styles from './ResultsPage.module.css';

/**
 * Шаг, с которого работа идёт по четвёрке, а не по рабочему набору (§33.2).
 *
 * Сравнение идёт по шагу, а не по `paid`: правка критериев внутри оплаченного
 * заказа возвращает его к анализу, `paid_at` при этом не сбрасывается, и
 * редирект «раз оплачено — на четвёрку» уводил бы с пересчитанного рабочего
 * набора на экран, которого у заказа сейчас нет.
 */
const SELECTION_STEP = 4;

/**
 * R-01 Результаты подбора (FE-080, FE-081, FE-082; MVP_PRD §12 и §19).
 *
 * Один экран покрывает весь путь после подтверждения критериев:
 *
 * 1. **ожидание.** Пока прогон `queued|processing`, статус опрашивается раз в
 *    секунду и показываются реальные этапы работы worker'а (FE-080);
 * 2. **открытая витрина рабочего набора** — то, что видно до оплаты (FE-586,
 *    Epic 58): данные автомобилей бесплатны, аналитика и работа Karl — нет;
 * 3. **пустые состояния.** «Ничего не прошло критерии» и «прогон не
 *    запускался» — разные ответы backend и разные экраны (AC-09); «Meine
 *    Favoriten» без отметок — третье, своё (BE-581).
 *
 * Запрос приходит из пути (`/app/searches/:searchId/results`), прогон — из
 * адреса (`?run=…`): ссылка на результаты воспроизводима, а перезагрузка
 * страницы не теряет подбор. Если параметра нет, берётся текущий прогон
 * **этого** запроса и записывается в адрес — результаты одного запроса никогда
 * не показываются в контексте другого (AC-07).
 *
 * **Это экран шага 2 и только его.** Оплаченный заказ уходит отсюда на
 * четвёрку: после оплаты работа идёт по четырём автомобилям, а не по сотне, и
 * второй список тех же машин с теми же кнопками — лишний шаг между оплатой и
 * тем, за что заплатили (MVP_PRD §33.2).
 *
 * **Фильтров здесь нет; сортировка — только у витрины и только по открытым
 * значениям** (BE-580): цена, пробег, год, новизна. Сужение выдачи — через
 * Karl и новый прогон. Порядок витрины по умолчанию — детерминированная
 * перестановка прогона, и он назван случайным (§34.11). Сортировка, страница
 * и «Meine Favoriten» живут в адресе; то, что до оплаты backend отклонил бы
 * (`match_desc`, порог Match Score и т. п.), выбрасывается из адреса до
 * запроса.
 */
export function ResultsPage() {
  const { t } = useTranslation();
  const { searchId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const parsedQuery = parseResultQuery(searchParams);

  const sessionQuery = useSearchSession(searchId || null);
  const session = sessionQuery.data ?? null;
  const sessionRunId = session?.session.current_search_run_id ?? null;

  // До оплаты экран — витрина (BE-580), и адрес приводится к тому, что она
  // принимает. Режим известен по заказу ещё до ответа результатов: иначе
  // `sort=match_desc` из старой ссылки успел бы уйти запросом и вернуться `400`.
  const showcaseMode = session !== null && !session.session.paid;
  const query = showcaseMode ? toShowcaseQuery(parsedQuery) : parsedQuery;
  const runId = query.run ?? sessionRunId;
  // Без ответа о заказе режим не выбрать; упавшее чтение заказа не держит
  // результаты — они уходят с адресом как есть.
  const modeKnown = session !== null || sessionQuery.isError;

  const runQuery = useSearchRun(runId);
  const run = runQuery.data;
  const readable = run !== undefined && isRunReadable(run);

  /**
   * Стадию заказа backend двигает в момент завершения прогона, а сам заказ не
   * опрашивается (§22). Перестройка оплаченного заказа ждёт новый прогон на
   * этом же экране (FE-595) и без перечитывания осталась бы здесь, хотя новая
   * четвёрка уже собрана. Один запрос на переход, а не таймер.
   */
  const runStatus = run?.status ?? null;
  const queryClient = useQueryClient();
  useEffect(() => {
    if (runStatus === null || runStatus === 'queued' || runStatus === 'processing') return;
    void queryClient.invalidateQueries({ queryKey: queryKeys.searchSession(searchId) });
  }, [queryClient, runStatus, searchId]);

  const resultsQuery = useSearchResults(
    runId,
    toResultsApiQuery(query, SEARCH_RESULTS_PAGE_SIZE),
    readable && modeKnown,
  );
  const results = resultsQuery.data;

  // Размер набора для реплик витрины. С «Meine Favoriten» `total` ответа —
  // число отмеченных, поэтому размер набора читается отдельной страницей в
  // одну карточку: число на экране — из API, а не из памяти вкладки.
  const setSizeQuery = useSearchResults(
    runId,
    { page: 1, pageSize: 1 },
    readable && showcaseMode && query.favoritesOnly,
  );
  const setSize = query.favoritesOnly ? setSizeQuery.data?.total : results?.total;

  // Пересчёт после смены ревизии каталога: старые результаты остаются на
  // экране, новый прогон опрашивается в фоне (MATCHING §10).
  const refreshRunId = results?.refresh_run_id ?? run?.refresh_run_id ?? null;
  const refreshQuery = useSearchRun(results?.is_stale === true ? refreshRunId : null);

  const confirmSearch = useConfirmSearch();

  // Панели фильтров на экране нет, но адрес их по-прежнему понимает: ссылку
  // можно собрать руками, и тогда backend честно ответит `NO_FILTER_MATCHES`.
  // Единственный выход из этого состояния — сбросить адрес.
  const resetFilters = useCallback(() => {
    setSearchParams(toResultSearchParams(resetResultFilters(query)));
  }, [query, setSearchParams]);

  const goToPage = (page: number) => {
    setSearchParams(toResultSearchParams({ ...query, page }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Смена порядка и «Meine Favoriten» начинают выдачу с первой страницы:
  // пятая страница другого порядка — другие автомобили, а не продолжение.
  const changeSort = (sort: ShowcaseSort | undefined) => {
    setSearchParams(toResultSearchParams({ ...query, sort, page: 1 }));
  };
  const changeFavoritesOnly = (favoritesOnly: boolean) => {
    setSearchParams(toResultSearchParams({ ...query, favoritesOnly, page: 1 }));
  };

  // Адрес приводится к тому, что показано на экране: прогон из активной сессии
  // переносится в адрес (без этого ссылка вела бы «на текущий подбор», а не на
  // конкретный), а у витрины выбрасывается то, что она не принимает, — битая
  // ссылка не остаётся в адресе и не уезжает дальше в карточку автомобиля.
  const searchString = searchParams.toString();
  useEffect(() => {
    const target = { ...query, run: query.run ?? sessionRunId ?? undefined };
    const next = toResultSearchParams(target);
    const runAdded = query.run === undefined && target.run !== undefined;
    const cleaned = showcaseMode && next.toString() !== searchString;
    if (runAdded || cleaned) setSearchParams(next, { replace: true });
  }, [query, sessionRunId, showcaseMode, searchString, setSearchParams]);

  // Страница за пределами выдачи — устаревший адрес, а не пустой результат.
  const totalPages = results?.total_pages ?? 0;
  useEffect(() => {
    if (resultsQuery.isSuccess && totalPages > 0 && query.page > totalPages) {
      setSearchParams(toResultSearchParams({ ...query, page: 1 }), { replace: true });
    }
  }, [resultsQuery.isSuccess, totalPages, query, setSearchParams]);

  /** Оболочка: шаг трекера и рельс Karl (FE-192, FE-193). */
  useAppShell({
    stage: session?.session.stage ?? null,
    paid: session?.session.paid ?? false,
    karl: {
      // Диалог живёт в рельсе: кнопки «изменить запрос» на экране больше нет,
      // уточнение делается здесь и пересобирает подбор целиком (§35.4).
      extra: <KarlDialogue searchId={searchId} />,
    },
  });

  // --- Заказ ушёл к четвёрке ------------------------------------------------

  // После оплаты работа идёт по четырём автомобилям: второй список тех же машин
  // с теми же кнопками — лишний шаг между оплатой и тем, за что заплатили
  // (MVP_PRD §33.2). Настоящая проверка прав — на backend, здесь навигация.
  const step = session === null ? null : stageToStep(session.session.stage);
  if (step !== null && step >= SELECTION_STEP) {
    return <Navigate to={ROUTES.searchTop4(searchId)} replace />;
  }

  // --- Подбор ещё не запускался --------------------------------------------

  if (sessionQuery.isPending && runId === null) {
    return (
      <Container>
        <div className={styles.stateWrap}>
          <SearchProgress run={null} />
        </div>
      </Container>
    );
  }

  if (runId === null) {
    return (
      <Container>
        <div className={styles.stateWrap}>
          {/* Кнопки «к диалогу» нет: диалог с Karl стоит рядом, в рельсе, и
              подтверждение критериев запускает подбор оттуда (FE-193). */}
          <StateBlock title={t('results.noRun.title')} description={t('results.noRun.text')} />
        </div>
      </Container>
    );
  }

  // --- Статус прогона -------------------------------------------------------

  if (runQuery.isPending) {
    return (
      <Container>
        <SearchProgress run={null} />
      </Container>
    );
  }

  if (runQuery.isError) {
    const notFound = runQuery.error instanceof ApiError && runQuery.error.status === 404;
    return (
      <Container>
        <div className={styles.stateWrap}>
          <StateBlock
            tone={notFound ? 'neutral' : 'error'}
            title={notFound ? t('results.notFound.title') : t('results.error.title')}
            description={notFound ? t('results.notFound.text') : t('results.error.text')}
            action={
              notFound ? undefined : (
                <Button
                  variant="secondary"
                  onClick={() => {
                    void runQuery.refetch();
                  }}
                >
                  {t('error.retry')}
                </Button>
              )
            }
          />
        </div>
      </Container>
    );
  }

  if (isRunPending(runQuery.data)) {
    return (
      <Container>
        <SearchProgress run={runQuery.data} />
      </Container>
    );
  }

  if (runQuery.data.status === 'failed') {
    // Подтверждённые критерии не теряются: повтор ставит новый прогон по той же
    // версии критериев, а не отправляет пользователя собирать запрос заново.
    const criteriaVersionId = runQuery.data.criteria_version_id;
    const sessionId = runQuery.data.search_session_id;
    return (
      <Container>
        <div className={styles.stateWrap}>
          <StateBlock
            tone="error"
            title={t('results.failed.title')}
            description={t('results.failed.text')}
            action={
              <div className={styles.stateActions}>
                <Button
                  disabled={confirmSearch.isPending}
                  onClick={() => {
                    confirmSearch.mutate(
                      { sessionId, criteriaVersionId, restart: true },
                      {
                        onSuccess: (accepted) => {
                          setSearchParams(
                            toResultSearchParams({
                              ...query,
                              run: accepted.search_run_id,
                              page: 1,
                            }),
                          );
                        },
                      },
                    );
                  }}
                >
                  {confirmSearch.isPending ? t('results.failed.retrying') : t('error.retry')}
                </Button>
              </div>
            }
          />
        </div>
      </Container>
    );
  }

  // Перестройка оплаченного заказа досчитана, а заказ ещё перечитывается:
  // рабочего набора у оплаченного заказа нет (MVP_PRD §35.4), и вместо
  // мелькнувшего списка сотни экран ждёт перехода на новую четвёрку (FE-595).
  if (session?.session.paid === true && sessionQuery.isFetching) {
    return (
      <Container>
        <SearchProgress run={runQuery.data} />
      </Container>
    );
  }

  // --- Результаты -----------------------------------------------------------

  const items = results?.items ?? [];
  // До оплаты экран показывает **открытую витрину** рабочего набора: другой
  // порядок и другая схема карточки (FE-586, BE-580). Режим определяет ответ
  // сервера, а не состояние фронта.
  const showcase = results?.showcase ?? false;
  const openItems = items.filter(isResultOpen);
  const fullItems = items.filter(isResultFull);
  const noFavorites = results?.empty_reason === 'NO_FAVORITES';
  // Карточка автомобиля получает прогон, порядок, «Meine Favoriten» и страницу:
  // по ним считается «Auto N von M», и по ним же «назад» вернёт в этот список.
  const vehicleLinkSearch = toVehicleLinkSearch({ ...query, run: runId });
  const vehicleHref = (vehicleId: string) => ROUTES.searchVehicle(searchId, vehicleId);

  const pagination =
    totalPages > 1 ? (
      <nav className={styles.pagination} aria-label={t('catalog.pagination.label')}>
        <Button
          variant="secondary"
          disabled={query.page <= 1}
          onClick={() => {
            goToPage(query.page - 1);
          }}
        >
          <CaretLeftIcon size={16} weight="bold" aria-hidden="true" />
          {t('catalog.pagination.previous')}
        </Button>

        <p className={`${styles.pageStatus} tabular`}>
          {t('catalog.pagination.status', { page: query.page, total: totalPages })}
        </p>

        <Button
          variant="secondary"
          disabled={query.page >= totalPages}
          onClick={() => {
            goToPage(query.page + 1);
          }}
          iconEnd={<CaretRightIcon size={16} weight="bold" />}
        >
          {t('catalog.pagination.next')}
        </Button>
      </nav>
    ) : null;

  // Витрина — пока экран не знает ответа, по заказу: скелетон повторяет ту
  // раскладку, которая сейчас появится.
  const showcaseLayout = results === undefined ? showcaseMode : showcase;

  return (
    <div className={styles.page}>
      <Container>
        <Link to={ROUTES.app} className={styles.back}>
          <ArrowLeftIcon size={16} weight="bold" aria-hidden="true" />
          {t('searches.backToList')}
        </Link>

        <ResultsSummary
          criteria={session?.criteria.payload ?? null}
          weights={session?.criteria.weights ?? []}
          total={resultsQuery.isSuccess ? results?.total : undefined}
          titleOnly={showcaseLayout}
        />

        {results?.is_stale === true ? (
          <StaleNotice
            refreshReady={refreshQuery.data !== undefined && isRunReadable(refreshQuery.data)}
            onShowRefreshed={() => {
              if (refreshRunId !== null) {
                setSearchParams(toResultSearchParams({ ...query, run: refreshRunId, page: 1 }));
              }
            }}
          />
        ) : null}

        <div className={styles.layout}>
          <div className={styles.results}>
            {resultsQuery.isPending ? (
              <>
                <p className="visually-hidden" role="status">
                  {t('results.loading')}
                </p>
                {showcaseLayout ? (
                  <ul className={styles.grid} aria-busy="true">
                    {Array.from({ length: 3 }, (_, index) => (
                      <li key={index}>
                        <VehicleCardSkeleton />
                      </li>
                    ))}
                  </ul>
                ) : (
                  <ul className={styles.list} aria-busy="true">
                    {Array.from({ length: 3 }, (_, index) => (
                      <li key={index}>
                        <ResultCardSkeleton />
                      </li>
                    ))}
                  </ul>
                )}
              </>
            ) : null}

            {resultsQuery.isError ? (
              <StateBlock
                tone="error"
                title={t('results.error.title')}
                description={t('results.error.text')}
                action={
                  <Button
                    variant="secondary"
                    onClick={() => {
                      void resultsQuery.refetch();
                    }}
                  >
                    {t('error.retry')}
                  </Button>
                }
              />
            ) : null}

            {results !== undefined &&
            items.length === 0 &&
            results.empty_reason !== null &&
            !noFavorites ? (
              <EmptyResults
                reason={results.empty_reason}
                limitingCriteria={results.limiting_criteria}
                suggestions={results.relaxation_suggestions}
                eligibleCount={runQuery.data.eligible_count}
                onResetFilters={resetFilters}
              />
            ) : null}

            {/* Витрина до оплаты (FE-586): реплики Karl, плашка, сортировка,
                открытые карточки, следующий шаг и нижняя строка — одним
                блоком фичи. Пагинация — та же, что у полного списка. */}
            {showcase && (openItems.length > 0 || noFavorites) ? (
              <Showcase
                searchId={searchId}
                runId={runId}
                items={openItems}
                setSize={setSize}
                favoritesCount={results?.favorites_count ?? 0}
                sort={isShowcaseSort(query.sort) ? query.sort : undefined}
                favoritesOnly={query.favoritesOnly}
                noFavorites={noFavorites}
                vehicleHref={vehicleHref}
                vehicleLinkSearch={vehicleLinkSearch}
                busy={resultsQuery.isFetching}
                pagination={pagination}
                onSortChange={changeSort}
                onFavoritesOnlyChange={changeFavoritesOnly}
              />
            ) : null}

            {!showcase && (fullItems.length > 0 || noFavorites) ? (
              <>
                {/* Отметки избранного живут на заказе и после оплаты те же
                    (BE-581): «Meine Favoriten» над списком, сердечко — на
                    карточках. */}
                <div className={styles.toolbar}>
                  <FavoritesFilterButton
                    count={results?.favorites_count ?? 0}
                    active={query.favoritesOnly}
                    onChange={changeFavoritesOnly}
                  />
                </div>
                {query.favoritesOnly ? (
                  <FavoritesOnlyNote
                    onShowAll={() => {
                      changeFavoritesOnly(false);
                    }}
                  />
                ) : null}

                {/* Единственный результат — не ошибка, но и не выбор:
                    об этом сказано прямо (MVP_PRD §19). */}
                {results?.total === 1 && !query.favoritesOnly ? (
                  <p className={styles.singleNote} role="note">
                    {t('results.single')}
                  </p>
                ) : null}

                {noFavorites ? (
                  <FavoritesEmpty
                    onShowAll={() => {
                      changeFavoritesOnly(false);
                    }}
                  />
                ) : (
                  <ul className={styles.list} aria-busy={resultsQuery.isFetching}>
                    {fullItems.map((item, index) => (
                      <Reveal as="li" key={item.vehicle_id} index={index % 3}>
                        <PaidResultCard
                          searchId={searchId}
                          runId={runId}
                          item={item}
                          index={index}
                          to={`${vehicleHref(item.vehicle_id)}${vehicleLinkSearch}`}
                        />
                      </Reveal>
                    ))}
                  </ul>
                )}

                {pagination}
              </>
            ) : null}

            {/* Оговорка о границах системы — рядом с выдачей: витрина собрана
                из каталога CarViQ.DE, поиска в интернете не было (CLAUDE.md §2). */}
            {showcase && openItems.length > 0 ? (
              <p className={styles.disclaimer}>{t('results.disclaimer')}</p>
            ) : null}
          </div>
        </div>
      </Container>
    </div>
  );
}
